import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

// Validation regex patterns
const VALIDATION_PATTERNS = {
  firstName: /^[a-zA-ZÀ-ÿ\s'-]{2,50}$/,
  lastName: /^[a-zA-ZÀ-ÿ\s'-]{2,50}$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^\+?[\d\s-()]+$/,
}

// Debounce helper
function debounce(fn, delay) {
  let timeoutId
  return (...args) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

export function useLeadGeneration(publicId) {
  const { t } = useI18n()

  // Field configuration based on environment variable (with DevTools override support)
  const leadRequired = computed(() => {
    // Check for DevTools override first (development mode only)
    const override = import.meta.env.DEV ? localStorage.getItem('devtools_lead_required_override') : null
    const value = override || import.meta.env.VITE_APP_LEAD_REQUIRED || 'both'
    const valid = ['email', 'phone', 'both']
    return valid.includes(value.toLowerCase()) ? value.toLowerCase() : 'both'
  })

  const fieldConfig = computed(() => ({
    showEmail: leadRequired.value === 'email' || leadRequired.value === 'both',
    showPhone: leadRequired.value === 'phone' || leadRequired.value === 'both',
    emailRequired: leadRequired.value === 'email',
    phoneRequired: leadRequired.value === 'phone',
    eitherRequired: leadRequired.value === 'both',
    termsRequired: import.meta.env.VITE_APP_TERMS_REQUIRED
  }))

  // Form data
  const formData = ref({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    privacyAccepted: false,
    termsAccepted: false,
    marketingOptIn: false, // Optional - not required for submission
  })

  // Validation errors
  const errors = ref({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    emailOrPhone: '',
    privacy: '',
    terms: '',
  })

  // Loading and submission state
  const isSubmitting = ref(false)
  const submitError = ref(null)
  const isSuccess = ref(false)

  // Check if form is valid
  const isFormValid = computed(() => {
    const hasFirstName = formData.value.firstName.trim() !== '' && !errors.value.firstName
    const hasLastName = formData.value.lastName.trim() !== '' && !errors.value.lastName
    const hasPrivacy = formData.value.privacyAccepted
    const hasTerms = fieldConfig.value.termsRequired === 'true' ? formData.value.termsAccepted : true

    let hasValidContact = false

    if (fieldConfig.value.emailRequired) {
      // Email-only mode: email must be filled and valid
      hasValidContact = formData.value.email.trim() !== '' && !errors.value.email
    } else if (fieldConfig.value.phoneRequired) {
      // Phone-only mode: phone must be filled and valid
      hasValidContact = formData.value.phone.trim() !== '' && !errors.value.phone
    } else {
      // Both mode: at least one must be filled and valid
      const hasEmail = formData.value.email.trim() !== '' && !errors.value.email
      const hasPhone = formData.value.phone.trim() !== '' && !errors.value.phone
      hasValidContact = (hasEmail || hasPhone) && !errors.value.emailOrPhone
    }

    return hasFirstName && hasLastName && hasValidContact && hasPrivacy && hasTerms
  })

  // Validate individual field
  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case 'firstName':
        if (!value) {
          errors.value.firstName = t('leadForm.validation.firstNameRequired')
        } else if (!VALIDATION_PATTERNS.firstName.test(value)) {
          errors.value.firstName = t('leadForm.validation.firstNameInvalid')
        } else {
          errors.value.firstName = ''
        }
        break

      case 'lastName':
        if (!value) {
          errors.value.lastName = t('leadForm.validation.lastNameRequired')
        } else if (!VALIDATION_PATTERNS.lastName.test(value)) {
          errors.value.lastName = t('leadForm.validation.lastNameInvalid')
        } else {
          errors.value.lastName = ''
        }
        break

      case 'email':
        if (fieldConfig.value.showEmail) {
          if (fieldConfig.value.emailRequired && !value.trim()) {
            errors.value.email = t('leadForm.validation.emailRequired')
          } else if (value.trim() && !VALIDATION_PATTERNS.email.test(value)) {
            errors.value.email = t('leadForm.validation.emailInvalid')
          } else {
            errors.value.email = ''
          }
        }
        // Check email or phone requirement for "both" mode
        if (fieldConfig.value.eitherRequired) {
          validateEmailOrPhone()
        }
        break

      case 'phone':
        if (fieldConfig.value.showPhone) {
          if (fieldConfig.value.phoneRequired && !value.trim()) {
            errors.value.phone = t('leadForm.validation.phoneRequired')
          } else if (value.trim()) {
            const digitsOnly = value.replace(/\D/g, '')
            if (!VALIDATION_PATTERNS.phone.test(value) || digitsOnly.length < 10) {
              errors.value.phone = t('leadForm.validation.phoneInvalid')
            } else {
              errors.value.phone = ''
            }
          } else {
            errors.value.phone = ''
          }
        }
        // Check email or phone requirement for "both" mode
        if (fieldConfig.value.eitherRequired) {
          validateEmailOrPhone()
        }
        break
    }
  }

  // Validate email or phone requirement
  const validateEmailOrPhone = () => {
    if (!formData.value.email && !formData.value.phone) {
      errors.value.emailOrPhone = t('leadForm.validation.emailOrPhoneRequired')
    } else if (
      (formData.value.email && errors.value.email) ||
      (formData.value.phone && errors.value.phone)
    ) {
      // If one field has content but is invalid, keep the error
      errors.value.emailOrPhone = t('leadForm.validation.emailOrPhoneRequired')
    } else {
      errors.value.emailOrPhone = ''
    }
  }

  // Debounced validation for typing
  const debouncedValidate = debounce((fieldName, value) => {
    validateField(fieldName, value)
  }, 500)

  // Handle field blur
  const handleBlur = (fieldName) => {
    validateField(fieldName, formData.value[fieldName])
  }

  // Handle field input with debouncing
  const handleInput = (fieldName, value) => {
    formData.value[fieldName] = value
    debouncedValidate(fieldName, value)
  }

  // 24 hours in milliseconds
  const EXPIRATION_TIME = 24 * 60 * 60 * 1000

  // Check if lead already submitted and not expired
  const checkLeadSubmitted = () => {
    try {
      const storedData = localStorage.getItem('lead_submitted')
      if (!storedData) {
        if (import.meta.env.DEV) {
          console.log('[Lead Check] No stored data found')
        }
        return false
      }

      const data = JSON.parse(storedData)

      // Check if data has required fields
      if (!data.submitted || !data.timestamp || !data.expiresAt) {
        if (import.meta.env.DEV) {
          console.warn('[Lead Check] Invalid data structure:', data)
        }
        // Invalid data structure, clean up
        localStorage.removeItem('lead_submitted')
        localStorage.removeItem('id_')
        return false
      }

      // Validate timestamp is not in the future (data corruption check)
      const now = Date.now()
      if (data.timestamp > now) {
        if (import.meta.env.DEV) {
          console.warn('[Lead Check] Timestamp is in the future - corrupted data:', {
            timestamp: new Date(data.timestamp),
            now: new Date(now),
          })
        }
        localStorage.removeItem('lead_submitted')
        localStorage.removeItem('id_')
        return false
      }

      // Check if expired (24 hours)
      const isExpired = now > data.expiresAt

      if (import.meta.env.DEV) {
        console.log('[Lead Check] Expiration check:', {
          now: new Date(now),
          expiresAt: new Date(data.expiresAt),
          isExpired,
          hoursRemaining: ((data.expiresAt - now) / (60 * 60 * 1000)).toFixed(2),
        })
      }

      if (isExpired) {
        if (import.meta.env.DEV) {
          console.log('[Lead Check] Data expired - cleaning up')
        }
        // Clean up expired data
        localStorage.removeItem('lead_submitted')
        localStorage.removeItem('id_')
        return false
      }

      if (import.meta.env.DEV) {
        console.log('[Lead Check] Valid submission found - skipping form')
      }

      return true
    } catch (error) {
      console.error('Error parsing lead_submitted data:', error)
      // Clean up corrupted data
      localStorage.removeItem('lead_submitted')
      localStorage.removeItem('id_')
      return false
    }
  }

  // Get the stored lead ID
  const getLeadId = () => {
    return localStorage.getItem('id_')
  }

  // Store submission success in localStorage with 24h expiration
  const storeSubmissionSuccess = (responseId = null) => {
    const now = Date.now()
    const data = {
      submitted: true,
      timestamp: now,
      expiresAt: now + EXPIRATION_TIME,
    }

    if (import.meta.env.DEV) {
      console.log('[Store] Saving lead submission:', {
        now,
        nowDate: new Date(now).toISOString(),
        expiresAt: data.expiresAt,
        expiresDate: new Date(data.expiresAt).toISOString(),
        responseId,
      })
    }

    localStorage.setItem('lead_submitted', JSON.stringify(data))

    // Store the response ID if provided
    if (responseId) {
      const idString = String(responseId)
      localStorage.setItem('id_', idString)

      if (import.meta.env.DEV) {
        console.log('[Store] Saved id_:', idString)
      }
    } else if (import.meta.env.DEV) {
      console.warn('[Store] No response ID provided')
    }
  }

  // Clear lead submission (for testing/reset)
  const clearLeadSubmission = () => {
    localStorage.removeItem('lead_submitted')
    localStorage.removeItem('id_')
  }

  // Submit form
  const submitForm = async () => {
    // Validate all fields
    validateField('firstName', formData.value.firstName)
    validateField('lastName', formData.value.lastName)
    validateField('email', formData.value.email)
    validateField('phone', formData.value.phone)

    // Validate checkboxes
    if (!formData.value.privacyAccepted) {
      errors.value.privacy = t('leadForm.privacy.required')
    } else {
      errors.value.privacy = ''
    }

    if (!formData.value.termsAccepted) {
      errors.value.terms = t('leadForm.terms.required')
    } else {
      errors.value.terms = ''
    }

    // Check if form is valid
    if (!isFormValid.value) {
      return false
    }

    isSubmitting.value = true
    submitError.value = null

    try {
      // Build payload for server-side API route
      // Server will add leadKey and agentId from environment variables
      const payload = {
        firstName: formData.value.firstName,
        lastName: formData.value.lastName,
        email: formData.value.email || '',
        phone: formData.value.phone || '',
        privacyAccepted: formData.value.privacyAccepted,
        termsAccepted: formData.value.termsAccepted,
        marketingOptIn: formData.value.marketingOptIn, // Include marketing consent
        publicId: publicId || '',
      }

      // Call server-side API route (keeps credentials private)
      const response = await fetch('/api/submit-lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
      }

      // Parse response to extract ID
      let responseData = {}
      let responseId = null

      try {
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          responseData = await response.json()

          // Try different response formats for ID extraction
          // Note: Server uses "id:" with colon, not "id"
          responseId =
            responseData.id ||
            responseData['id:'] || // Handle "id:" key with colon
            responseData.leadId ||
            responseData['leadId:'] ||
            responseData.data?.id ||
            responseData.data?.['id:'] ||
            null

          // Convert to string if it's a number
          if (responseId !== null && typeof responseId === 'number') {
            responseId = String(responseId)
          }
        } else {
          // Handle text response
          const text = await response.text()

          // Try to extract ID from text using regex patterns
          // Updated to handle "id:" format
          const idMatch =
            text.match(/["']?id["']?\s*:\s*["']?([a-zA-Z0-9-_]+)["']?/i) ||
            text.match(/["']?leadId["']?\s*:\s*["']?([a-zA-Z0-9-_]+)["']?/i) ||
            text.match(/id[:\s]+["']?([a-zA-Z0-9-_]+)["']?/i) || 
            text.match(/id["\s:]+["']?([a-zA-Z0-9-_]+)["']?/i) ||
            text.match(/leadId["\s:]+["']?([a-zA-Z0-9-_]+)["']?/i)

          if (idMatch) {
            responseId = idMatch[1]
          }
        }
      } catch (parseError) {
        if (import.meta.env.DEV) {
          console.warn('Could not parse response for ID:', parseError)
        }
        // Continue without ID - submission still successful
      }

      // Success - store in localStorage with 24h expiration
      storeSubmissionSuccess(responseId)
      isSuccess.value = true

      // Log success with ID info
      if (import.meta.env.DEV) {
        console.log('Lead submission successful:', {
          responseData,
          extractedId: responseId,
          expiresAt: new Date(Date.now() + EXPIRATION_TIME).toISOString(),
          fullResponse: responseData,
        })
      }

      return true
    } catch (error) {
      console.error('Lead submission error:', error)
      submitError.value = error.message || t('leadForm.error.genericMessage')
      return false
    } finally {
      isSubmitting.value = false
    }
  }

  // Reset form
  const resetForm = () => {
    formData.value = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      privacyAccepted: false,
      termsAccepted: false,
      marketingOptIn: false,
    }

    errors.value = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      emailOrPhone: '',
      privacy: '',
      terms: '',
    }

    submitError.value = null
    isSuccess.value = false
  }

  return {
    formData,
    errors,
    isSubmitting,
    submitError,
    isSuccess,
    isFormValid,
    fieldConfig,
    validateField,
    handleBlur,
    handleInput,
    submitForm,
    resetForm,
    checkLeadSubmitted,
    clearLeadSubmission,
    getLeadId,
    storeSubmissionSuccess,
  }
}
